import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const About = () => {
  const teamMembers = [
    {
      name: 'Michael Rodriguez',
      position: 'Founder & CEO',
      experience: '15+ years',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'Passionate about eco-friendly cleaning solutions and customer satisfaction.',
      specialties: ['Eco-Friendly Solutions', 'Business Management', 'Customer Service']
    },
    {
      name: 'Sarah Johnson',
      position: 'Head Technician',
      experience: '12+ years',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'Certified cleaning expert with extensive knowledge in stain removal techniques.',
      specialties: ['Stain Removal', 'Advanced Cleaning', 'Quality Control']
    },
    {
      name: 'David Chen',
      position: 'Operations Manager',
      experience: '10+ years',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'Ensures smooth operations and maintains our high service standards.',
      specialties: ['Operations', 'Team Management', 'Process Optimization']
    },
    {
      name: 'Emily Williams',
      position: 'Customer Relations',
      experience: '8+ years',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'Dedicated to providing exceptional customer experience and support.',
      specialties: ['Customer Service', 'Scheduling', 'Client Relations']
    }
  ];

  const milestones = [
    {
      year: '2019',
      title: 'Company Founded',
      description: 'Started with a single van and a vision for eco-friendly cleaning'
    },
    {
      year: '2020',
      title: 'Team Expansion',
      description: 'Grew to 10 team members and expanded service area'
    },
    {
      year: '2022',
      title: 'Eco-Certification',
      description: 'Achieved Green Clean certification for all our products'
    },
    {
      year: '2023',
      title: 'Technology Upgrade',
      description: 'Implemented advanced cleaning equipment and scheduling software'
    },
    {
      year: '2024',
      title: 'Industry Leader',
      description: 'Served over 10,000 satisfied customers across the region'
    }
  ];

  const values = [
    {
      icon: '🌱',
      title: 'Sustainability',
      description: 'We prioritize environmentally friendly practices and products in every aspect of our business.',
      features: ['Eco-certified products', 'Water conservation', 'Waste reduction']
    },
    {
      icon: '⭐',
      title: 'Excellence',
      description: 'We never compromise on quality and continuously strive to exceed customer expectations.',
      features: ['Certified technicians', 'Quality assurance', 'Continuous training']
    },
    {
      icon: '🤝',
      title: 'Integrity',
      description: 'Honest pricing, transparent processes, and reliable service you can trust.',
      features: ['No hidden fees', 'Upfront quotes', 'Honest assessments']
    },
    {
      icon: '💡',
      title: 'Innovation',
      description: 'We embrace new technologies and methods to deliver superior cleaning results.',
      features: ['Latest equipment', 'Advanced techniques', 'Process optimization']
    }
  ];

  const certifications = [
    { name: 'IICRC Certified', icon: '🏆' },
    { name: 'Green Clean Certified', icon: '🌎' },
    { name: 'EPA Safer Choice', icon: '🛡️' },
    { name: 'Better Business Bureau A+', icon: '⭐' },
    { name: 'Local Business Award 2023', icon: '📜' },
    { name: 'Customer Choice Award', icon: '❤️' }
  ];

  return (
    <>
      <SEO 
        title="About GreenRocky Carpet - Our Story & Mission"
        description="Learn about GreenRocky Carpet's commitment to eco-friendly carpet cleaning. 15+ years of experience serving homeowners and businesses with green cleaning solutions."
        keywords="about us, carpet cleaning company, eco-friendly mission, green cleaning, professional team"
        url="https://greenrockycarpet.com.au/about"
        section="About"
      />

    <div>
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">About Us</h1>
            <p className="hero-subtitle">
              Leading the way in eco-friendly carpet cleaning with over a decade of excellence
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <div className="stat-number">5+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="hero-stat">
                <div className="stat-number">6K+</div>
                <div className="stat-label">Happy Customers</div>
              </div>
              <div className="hero-stat">
                <div className="stat-number">7.1K+</div>
                <div className="stat-label">Carpets Cleaned</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-content">
              <div className="section-header">
                <div className="section-tag">Our Journey</div>
                <h2 className="section-title">Building a Legacy of Carpet Clean</h2>
              </div>
              <div className="story-text">
                <p>
                  Founded in 2019 by Mr. Mujibur Rahman, GreenRocky Carpet started as a small 
                  family business with a single van and a powerful vision: to provide exceptional 
                  carpet cleaning services while protecting our environment.
                </p>
                <p>
                  What began as a one-person operation has blossomed into a team of dedicated 
                  professionals serving the entire region. Our commitment to using only 
                  eco-friendly products and advanced cleaning techniques has set us apart 
                  in the industry.
                </p>
                <p>
                  Today, we're proud to be the trusted choice for thousands of homeowners 
                  and businesses who value both sparkling clean carpets and environmental 
                  responsibility.
                </p>
              </div>
              <div className="story-features">
                <div className="story-feature">
                  <span className="feature-icon">🌿</span>
                  <span>100% Eco-Friendly Products</span>
                </div>
                <div className="story-feature">
                  <span className="feature-icon">⚡</span>
                  <span>Latest Cleaning Technology</span>
                </div>
                <div className="story-feature">
                  <span className="feature-icon">🛡️</span>
                  <span>Fully Insured & Certified</span>
                </div>
              </div>
            </div>
            <div className="story-visual">
              <div className="image-stack">
                <img 
                  src="/images/story-1.jpg" 
                  alt="Our team at work" 
                  className="stack-image main"
                />
                <img 
                  src="/images/story-2.jpg" 
                  alt="Cleaning equipment" 
                  className="stack-image secondary"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Our Growth</div>
            <h2 className="section-title">Milestones & Achievements</h2>
          </div>
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-content">
                  <div className="timeline-year">{milestone.year}</div>
                  <h3 className="timeline-title">{milestone.title}</h3>
                  <p className="timeline-description">{milestone.description}</p>
                </div>
                <div className="timeline-dot"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Our Promise</div>
            <h2 className="section-title">Core Values That Drive Us</h2>
            <p className="section-subtitle">
              These principles guide every decision we make and every service we provide
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
                <ul className="value-features">
                  {value.features.map((feature, idx) => (
                    <li key={idx} className="value-feature">
                      <span className="check-icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Certifications Section */}
      <section className="certifications-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Certifications & Awards</h2>
            <p className="section-subtitle">
              Recognized for excellence in service and environmental responsibility
            </p>
          </div>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-item">
                <div className="cert-icon">{cert.icon}</div>
                <span className="cert-name">{cert.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Experience the GreenRocky Difference?</h2>
            <p className="cta-description">
              Join our family of satisfied customers and discover why we're the trusted choice 
              for eco-friendly carpet cleaning in the region.
            </p>
            <div className="cta-buttons">
              <Link to="/booking" className="cta-btn primary">
                Book Your Service
              </Link>
              <Link to="/contact" className="cta-btn secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;